import paper from 'paper/dist/paper-core'
import { Injectable } from '@angular/core'
import {
  CycleBoundaryDrawable,
  Edge,
  LineSide,
  Mesh,
  MeshPoint,
  SNAPPING_ERROR_TOLERANCE,
  WallMesh,
} from 'formwork-planner-lib'
import { Model } from '../../planner/model/Model'
import { Cycle } from '../model/Cycle'
import { CycleSegment } from '../model/CycleSegment'
import {
  CYCLE_BOUNDARIES_MIN_DISTANCE,
  CYCLE_CROSSHAIR_SNAPPING_DISTANCE,
} from '../../planner/model/snapping/constants'
import { snapPointToRastering } from '../../../utils/pathUtils'
import { defaultDrawSettings } from '../../../models/draw-settings'
import { LengthLabel } from '../../planner/model/paper/LengthLabel'

/**
 * Contains the information needed to calculate the cycles - the created cycles, the list of cycle boundaries and the wall model
 */
interface CycleCalculationData {
  cycles: Readonly<Cycle[]>
  cycleBoundaries: Readonly<CycleBoundaryDrawable[]>
  model: Readonly<Model<WallMesh>>
}

/**
 * Service used to calculate the cycles from a wall model and its cycle boundaries
 */
@Injectable()
export class CycleCalculationService {
  /**
   * Searches for cycles without cycle boundaries
   * @param edgesWithBoundaries - edges which have a boundary
   */
  private findDefaultCycles(
    model: Model<WallMesh>,
    edgesWithBoundaries: Set<Edge>,
    cyclesWithBoundaries: Cycle[]
  ): Cycle[] {
    const cycles: Cycle[] = []
    let connectedEdgeMeshes = model.mesh.groupConnectedEdges()
    connectedEdgeMeshes = connectedEdgeMeshes.filter(
      (mesh) => ![...mesh.getAllEdges()].some((edge) => edgesWithBoundaries.has(edge))
    )

    for (const edgeGroup of connectedEdgeMeshes) {
      const cycle = new Cycle([], 1)
      for (const edge of edgeGroup.getAllEdges()) {
        const segment = new CycleSegment(edge.startPoint, edge.endPoint)
        if (this.isNewSegment(cyclesWithBoundaries, segment)) {
          cycle.segments.push(segment)
        }
      }

      if (cycle.segments.length > 0) {
        cycles.push(cycle)
      }
    }

    return cycles
  }

  /**
   * searches for neighboring cycles of the current cycle, used to ensure that no adjacent cycles have the same numbers
   * @param cycles all cycles in the model
   * @param currentCycle the current cycle
   */
  private findNeighbourCyclesIn(cycles: Cycle[], currentCycle: Cycle): Cycle[] {
    const result: Cycle[] = []
    cycles
      .filter((it) => it !== currentCycle)
      .forEach((cycle) => {
        if (cycle.isNeighbourTo(currentCycle) && !result.includes(cycle)) {
          result.push(cycle)
        }
      })
    return result
  }

  /**
   * Traverses the walls from a given starting point searching for either another cycle boundary or an open ended wall and creates segments for a cycle in the process
   * @param data the object containing the cycles, the cycle boundaries and the wallmodel
   * @param startPoint the starting point of the process (and the first created cycle segment)
   * @param currentEndPoint the end point of the first cycle segment of the created cycle
   */
  private createCycle(
    data: CycleCalculationData,
    startPoint: CycleBoundaryDrawable,
    currentEndPoint: CycleBoundaryDrawable | MeshPoint
  ): Cycle | null {
    const { cycles, model } = data
    const segment = new CycleSegment(startPoint, currentEndPoint)
    const cycle = new Cycle([segment], 1)

    const isNewSegment = this.isNewSegment(cycles, segment)
    if (!isNewSegment) {
      return null
    }

    // case only happens if the cycle boundary is on a disconnected t-wall, see https://dev.azure.com/Umdasch-Group/Doka-ESD-EFP/_wiki/wikis/Doka-ESD-EFP.wiki/7302/T-walls-with-different-thickness?anchor=cycle-boundaries-on-disconnected-t-walls
    if (
      segment.getLength() === 0 &&
      currentEndPoint instanceof MeshPoint &&
      currentEndPoint.edges.size === 1
    ) {
      const currentEdge = [...currentEndPoint.edges][0]
      const disconnectedNeighbors = currentEdge.getCloseDisconnectedNeighbors(
        model.mesh.getAllEdges()
      )
      if (disconnectedNeighbors.length === 0) {
        return null
      }

      const disconnectedNeighbor = disconnectedNeighbors[0]
      const disconnectedPoints = disconnectedNeighbor.getCloseDisconnectedPoints(currentEdge)
      if (!disconnectedPoints) {
        return null
      }

      return this.createCycle(data, startPoint, disconnectedPoints[0])
    }

    // change cycle number according to already existing cycle boundary
    const side = startPoint.getClosestCircle(currentEndPoint)
    if (side === LineSide.LEFT) {
      cycle.cycleNumber = startPoint.cycleSymbolLeft.cycleNumber
    } else {
      cycle.cycleNumber = startPoint.cycleSymbolRight.cycleNumber
    }

    if (currentEndPoint instanceof MeshPoint) {
      const currentEdge = model.getNearestEdge(startPoint.getCenter())
      if (currentEdge) {
        const neighbours = currentEdge
          .getNeighbours()
          .filter(
            (edge: Edge) => edge.startPoint === currentEndPoint || edge.endPoint === currentEndPoint
          )

        // handling the special case during wall traversal described at https://dev.azure.com/Umdasch-Group/Doka-ESD-EFP/_wiki/wikis/Doka-ESD-EFP.wiki/7225/Drawing-walls-from-the-Model?anchor=special-cases
        const disconnectedNeighbours = new Set<[Edge, MeshPoint]>()
        model.getAllEdges().forEach((other) => {
          const disconnectedPoints = other.getCloseDisconnectedPoints(currentEdge)
          if (disconnectedPoints?.includes(currentEndPoint)) {
            disconnectedNeighbours.add([other, disconnectedPoints[0]])
          }
        })

        neighbours.forEach((neighbour: Edge) =>
          this.findSegmentsForCycle(data, cycle, currentEndPoint, neighbour, [])
        )
        disconnectedNeighbours.forEach(([neighbour, end]) =>
          this.findSegmentsForCycle(data, cycle, end, neighbour, [])
        )
      }
    }
    return cycle
  }

  /**
   * Returns true if no cycle already contains the cycle segment
   * @param cycles all cycles
   * @param newSegment the new segment to test for
   */
  private isNewSegment(cycles: Readonly<Cycle[]>, newSegment: CycleSegment): boolean {
    return !cycles.some((cycle) => cycle.segments.some((segment) => segment.equals(newSegment)))
  }

  /**
   * Recursive wall traversal function to find the neighboring cycle segments and add them to the current cycle
   * @param data containing the cycles, cycle boundaries and the walls of the model
   * @param currentCycle the cycle the cycle segments are added to
   * @param startPoint a mesh point from which the wall traversal is started
   * @param edge the edge the wall traversal is started on
   * @param visitedEdges array containing edges already visited to avoid endless loops
   */
  private findSegmentsForCycle(
    data: CycleCalculationData,
    currentCycle: Cycle,
    startPoint: MeshPoint,
    edge: Edge,
    visitedEdges: Edge[]
  ): void {
    const { cycleBoundaries, model, cycles } = data
    const cyclesBoundariesOnEdge = cycleBoundaries.filter(
      (boundary) => model.getNearestEdge(boundary.getCenter()) === edge
    )

    // if another cycle boundary is found on the edge, the cycle can be closed and the recursion stops
    if (cyclesBoundariesOnEdge.length !== 0) {
      let closestBoundary: CycleBoundaryDrawable | undefined
      cyclesBoundariesOnEdge.forEach((boundary) => {
        if (
          !closestBoundary ||
          boundary.getDistance(startPoint) <= closestBoundary.getDistance(startPoint)
        ) {
          closestBoundary = boundary
        }
      })
      if (closestBoundary) {
        const newSegment = new CycleSegment(startPoint, closestBoundary)
        if (this.isNewSegment(cycles, newSegment) && newSegment.getLength() > 0) {
          // set currentCycles cycle number according to newSegment
          const side = closestBoundary.getClosestCircle(startPoint)
          if (side === LineSide.LEFT) {
            currentCycle.cycleNumber = closestBoundary.cycleSymbolLeft.cycleNumber
          } else {
            currentCycle.cycleNumber = closestBoundary.cycleSymbolRight.cycleNumber
          }

          currentCycle.segments.push(newSegment)
        }
      }
    } else {
      // if no cycle boundary is found on the edge, the recursion continues on neighboring edges
      const newSegment = new CycleSegment(startPoint, edge.getOtherPoint(startPoint))
      if (this.isNewSegment(cycles, newSegment) && newSegment.getLength() > 0) {
        currentCycle.segments.push(newSegment)
      }

      visitedEdges.push(edge)
      const otherPoint = edge.getOtherPoint(startPoint)
      const neighbours = edge
        .getNeighbours()
        .filter((neighbour: Edge) => neighbour.hasPoint(otherPoint))
        .filter((visited: Edge) => !visitedEdges.includes(visited))

      // handling the special case during wall traversal described at https://dev.azure.com/Umdasch-Group/Doka-ESD-EFP/_wiki/wikis/Doka-ESD-EFP.wiki/7225/Drawing-walls-from-the-Model?anchor=special-cases
      const disconnectedNeighbours = new Set<[Edge, MeshPoint]>()
      model.getAllEdges().forEach((other) => {
        const disconnectedPoints = other.getCloseDisconnectedPoints(edge)
        if (!visitedEdges.includes(other) && disconnectedPoints?.includes(otherPoint)) {
          disconnectedNeighbours.add([other, disconnectedPoints[0]])
        }
      })

      // recursive calls to continue the process on neighboring edges
      neighbours.forEach((neighbour: Edge) =>
        this.findSegmentsForCycle(data, currentCycle, otherPoint, neighbour, visitedEdges)
      )

      disconnectedNeighbours.forEach(([neighbour, end]) =>
        this.findSegmentsForCycle(data, currentCycle, end, neighbour, visitedEdges)
      )
    }
  }

  private isTooCloseToOtherBoundary(
    snappingLine: paper.Path.Line | undefined,
    snapPoint: paper.Point | undefined,
    boundaries: CycleBoundaryDrawable[]
  ): boolean {
    if (snappingLine === undefined || snapPoint === undefined) {
      return false
    }

    const closeBoundary = boundaries.find((boundary) => {
      const boundaryCenter = boundary.getCenter()
      const closestPoint = snappingLine.getNearestPoint(boundaryCenter)
      if (!closestPoint || closestPoint.getDistance(boundaryCenter) > 1) {
        //not on same edge
        return false
      }
      return boundaryCenter.getDistance(snapPoint) < CYCLE_BOUNDARIES_MIN_DISTANCE
    })

    return closeBoundary != null
  }

  /**
   * Finds the cycles based on the model and the list of cycle boundaries by splitting up the wallmodel to cycle segments and assigning the segments to a cycle
   * @param cycleBoundaries list of cycle boundaries
   * @param model the wallmodel
   */
  public findCycles(cycleBoundaries: CycleBoundaryDrawable[], model: Model<WallMesh>): Cycle[] {
    const cycles: Cycle[] = []
    const hasBoundaries = new Set<Edge>()
    const data: CycleCalculationData = {
      cycles,
      cycleBoundaries,
      model,
    }

    // for each cycle boundary, two cycles are created left and right from the cycle boundary and the walls of the model are traversed, adding all cycle segments to these created cycles
    cycleBoundaries.forEach((boundary) => {
      const currentEdge = model.getNearestEdge(boundary.getCenter())
      if (currentEdge) {
        hasBoundaries.add(currentEdge)
        let leftCycleBoundary: CycleBoundaryDrawable | undefined
        let rightCycleBoundary: CycleBoundaryDrawable | undefined
        cycleBoundaries
          .filter((otherCycleBoundary) => otherCycleBoundary !== boundary)
          .forEach((otherBoundary) => {
            // if two cycle boundaries are on the same edge
            if (model.getNearestEdge(otherBoundary.getCenter()) === currentEdge) {
              if (
                otherBoundary.isLeftFrom(boundary) &&
                (!leftCycleBoundary ||
                  otherBoundary.getDistance(boundary) <= leftCycleBoundary.getDistance(boundary))
              ) {
                leftCycleBoundary = otherBoundary
              }
              if (
                !otherBoundary.isLeftFrom(boundary) &&
                (!rightCycleBoundary ||
                  otherBoundary.getDistance(boundary) <= rightCycleBoundary.getDistance(boundary))
              ) {
                rightCycleBoundary = otherBoundary
              }
            }
          })

        const firstCycle = this.createCycle(
          data,
          boundary,
          leftCycleBoundary ?? currentEdge.startPoint
        )

        if (firstCycle != null) {
          cycles.push(firstCycle)
        }

        const secondCycle = this.createCycle(
          data,
          boundary,
          rightCycleBoundary ?? currentEdge.endPoint
        )
        if (secondCycle != null) {
          cycles.push(secondCycle)
        }
      }
    })

    // cycles are created without cycle boundaries too
    const defaultCycles = this.findDefaultCycles(model, hasBoundaries, cycles)
    cycles.push(...defaultCycles)

    return cycles
  }

  public findCurrentCycle(
    cycles: Cycle[],
    model: Model<Mesh>,
    point: paper.Point
  ): Cycle | undefined {
    const nearestEdge = model.getNearestEdge(point)
    let result: Cycle | undefined
    if (nearestEdge) {
      cycles.forEach((cycle) => {
        if (cycle.containsPoint(point, nearestEdge.thickness / 2)) {
          result = cycle
        }
      })
    }
    return result
  }

  /**
   * Updates the numbers on the newly calculated cycles so that no adjacent cycles have the same number
   * @param cycles all cycles
   * @param updatedCycles the cycles to update the number of (only cycles should be changed that were created by adding a new cycle boundary - old cycles should keep their numbers)
   */
  public setNewNumbers(cycles: Cycle[], updatedCycles: Cycle[]): void {
    updatedCycles.forEach((cycle) => {
      const neighbours = this.findNeighbourCyclesIn(cycles, cycle)
      const neighbourNumbers = neighbours.map((elem) => elem.cycleNumber)

      // the lowest number is chosen that no neighboring cycle has
      let newNumber = 1
      while (neighbourNumbers.includes(newNumber)) {
        newNumber++
      }
      cycle.cycleNumber = newNumber
    })

    updatedCycles.forEach((cycle) => {
      this.changeNumber(cycle, cycle.cycleNumber)
    })
  }

  /**
   * Deletes the given cycle boundary and resets the numbers
   *
   * @param cycleBoundaries - array which the deleted cycle boundary is currently in
   * @param deletedBoundary - cycle boundary to be deleted
   */
  public deleteCycleBoundary(
    cycleBoundaries: CycleBoundaryDrawable[],
    deletedBoundary: CycleBoundaryDrawable,
    model: Model<WallMesh>
  ): CycleBoundaryDrawable[] {
    const newBoundaries = cycleBoundaries.filter((boundary) => boundary.id !== deletedBoundary.id)
    const newCycles = this.findCycles(newBoundaries, model)
    const cycleToChange = newCycles.find((cycle) => {
      const edgeOfDeleted = model.getNearestEdge(deletedBoundary.getCenter())
      if (edgeOfDeleted) {
        return cycle.containsPoint(deletedBoundary.getCenter(), edgeOfDeleted.thickness)
      } else {
        return false
      }
    })

    if (cycleToChange) {
      this.setNewNumbers(newCycles, [cycleToChange])
    }
    return newBoundaries
  }

  /**
   * Changes the cycle numbers of all relevant cycle boundaries of a cycle
   * @param cycle the cycle with the changed number
   * @param newNumber the new number
   */
  public changeNumber(cycle: Cycle, newNumber: number): void {
    cycle.cycleNumber = newNumber
    // every segment of the cycle is traversed, searching for cycle boundaries
    // the cycle number on the correct side of the cycle boundaries then needs to be changed
    cycle.segments.forEach((segment) => {
      if (segment.startPoint instanceof CycleBoundaryDrawable) {
        const side = segment.startPoint.getClosestCircle(segment.endPoint)
        if (side === LineSide.LEFT) {
          segment.startPoint.cycleSymbolLeft.cycleNumber = cycle.cycleNumber
        } else {
          segment.startPoint.cycleSymbolRight.cycleNumber = cycle.cycleNumber
        }
      }
      if (segment.endPoint instanceof CycleBoundaryDrawable) {
        const side = segment.endPoint.getClosestCircle(segment.startPoint)
        if (side === LineSide.LEFT) {
          segment.endPoint.cycleSymbolLeft.cycleNumber = cycle.cycleNumber
        } else {
          segment.endPoint.cycleSymbolRight.cycleNumber = cycle.cycleNumber
        }
      }
    })
  }

  public getBoundaryLine(position: paper.Point, edge: Edge): CycleBoundaryDrawable {
    const tangent = edge.getDirection().normalize().rotate(90)

    const start = position.add(tangent.multiply(edge.thickness / 2))
    const end = position.subtract(tangent.multiply(edge.thickness / 2))

    return new CycleBoundaryDrawable({
      id: 0,
      start,
      end,
      cycleNumberLeft: 1,
      cycleNumberRight: 1,
    })
  }

  /** shorten a snapping line to stay distant from boundaries and wall endings.
   *
   * @param originalLine the line to shorten
   * @param movingBoundary the moving boundary that does not need to be kept distance from
   */
  public shortenSnappingLine(
    originalLine: paper.Path.Line,
    movingBoundary: CycleBoundaryDrawable,
    cycleBoundaryDrawables: CycleBoundaryDrawable[]
  ): paper.Path.Line {
    const line = new paper.Path.Line(originalLine.firstCurve.point1, originalLine.firstCurve.point2)
    const center = line.getNearestPoint(movingBoundary.getCenter())
    const obstacles = cycleBoundaryDrawables
      .filter((boundary) => !boundary.equals(movingBoundary))
      .filter(
        (boundary) =>
          line.getNearestPoint(boundary.getCenter()).getDistance(boundary.getCenter()) <= 1
      )
      .map((boundary) => line.getNearestPoint(boundary.getCenter()))

    if (obstacles.length === 0) {
      return line
    }
    const closestObstacles = obstacles.sort((a, b) => a.getDistance(center) - b.getDistance(center))
    const leftClosestObstable = closestObstacles.find((o) => {
      return line.getOffsetOf(o) < line.getOffsetOf(center)
    })
    const rightClosestObstable = closestObstacles.find(
      (o) => line.getOffsetOf(o) > line.getOffsetOf(center)
    )

    // Cycle boundaries can only be placed a 100cm from each other or the end of walls, to ensure that a cylce is at least a 100cm long
    if (leftClosestObstable != null) {
      const vector = center.subtract(leftClosestObstable).normalize(CYCLE_BOUNDARIES_MIN_DISTANCE)
      line.firstCurve.point1 = leftClosestObstable.add(vector)
    }

    if (rightClosestObstable != null) {
      const vector = center.subtract(rightClosestObstable).normalize(CYCLE_BOUNDARIES_MIN_DISTANCE)
      line.firstCurve.point2 = rightClosestObstable.add(vector)
    }

    return line
  }

  public findCyclesForBoundary(cycles: Cycle[], boundary: CycleBoundaryDrawable): Cycle[] {
    return cycles.filter((cycle) => {
      return cycle.containsPoint(boundary.getCenter(), SNAPPING_ERROR_TOLERANCE)
    })
  }

  public snapPosition(
    position: paper.Point,
    snappingLines: paper.Path.Line[],
    boundaries: CycleBoundaryDrawable[],
    lengthRastering: number = defaultDrawSettings.lengthRastering
  ): paper.Point | undefined {
    let closestSnapPoint: paper.Point | undefined
    let closestLine: paper.Path.Line | undefined
    snappingLines.forEach((line) => {
      const snapPoint = snapPointToRastering(position, line, lengthRastering)
      if (
        !closestSnapPoint ||
        snapPoint.getDistance(position) < closestSnapPoint.getDistance(position)
      ) {
        closestSnapPoint = snapPoint
        closestLine = line
      }
    })

    const isTooClose = this.isTooCloseToOtherBoundary(closestLine, closestSnapPoint, boundaries)

    if (
      closestSnapPoint &&
      closestLine &&
      !isTooClose &&
      closestSnapPoint.getDistance(position) < CYCLE_CROSSHAIR_SNAPPING_DISTANCE
    ) {
      return closestSnapPoint
    }
    return undefined
  }

  public getClosestCycleBoundaryToLabel(
    model: Model<WallMesh>,
    boundaries: CycleBoundaryDrawable[],
    label: LengthLabel
  ): CycleBoundaryDrawable | undefined {
    const edge = model.getNearestParallelEdge(label.centerPoint, label.getDirectionVector())
    if (!edge) {
      return undefined
    }

    const closeBoundaries = boundaries
      .filter((boundary) => model.getNearestEdge(boundary.getCenter()) === edge)
      .sort(
        (boundary1, boundary2) =>
          label.position.getDistance(boundary1.getCenter()) -
          label.position.getDistance(boundary2.getCenter())
      )

    return closeBoundaries[0]
  }

  public isCycleBoundaryForLengthLabel(
    model: Model<WallMesh>,
    boundaries: CycleBoundaryDrawable[],
    boundaryToProve: CycleBoundaryDrawable,
    label: LengthLabel
  ): boolean {
    const labelBoundary = this.getClosestCycleBoundaryToLabel(model, boundaries, label)
    if (!labelBoundary) {
      return false
    }

    return this.isCycleBoundaryForLengthLabelWithLabelBoundary(
      labelBoundary,
      boundaryToProve,
      label
    )
  }

  public isCycleBoundaryForLengthLabelWithLabelBoundary(
    labelBoundary: CycleBoundaryDrawable,
    boundaryToProve: CycleBoundaryDrawable,
    label: LengthLabel
  ): boolean {
    const distanceToProve = label.position.getDistance(boundaryToProve.getCenter())
    const distanceToLabelBoundary = label.position.getDistance(labelBoundary.getCenter())
    if (Math.abs(distanceToLabelBoundary - distanceToProve) < SNAPPING_ERROR_TOLERANCE) {
      return true
    } else {
      return false
    }
  }
}
